import React, { useCallback } from 'react'
import { ButtonSpinner } from '@components/Login/LoginButton/Spinner/ButtonSpinner'
import { ActionNavItem } from '@components/NavItem/ActionNavItem'
import { useShouldShowRenewMembership } from '@components/Web3/RenewMembership/hooks/useRenewCountdown'
import { useRenewMyMembership } from '@components/Web3/RenewMembership/hooks/useRenewMyMembership'
import { RenewCountdownCircle } from '@components/Web3/RenewMembership/RenewCountdown'
import { Box, Text } from '@ui'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { useCombinedAuth } from 'privy/useCombinedAuth'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { BlockchainTransactionType, useIsTransactionPending } from 'use-towns-client'

import { SpaceSideBarListItem } from './SpaceSideBarListItem'

export function RenewMembershipItem() {
    const spaceId = useSpaceIdFromPathname()
    const { renewMembership, isLoading: isLoadingRenewMembership } = useRenewMyMembership({
        location: 'reminder',
    })
    const shouldShowRenewMembership = useShouldShowRenewMembership({ spaceId: spaceId ?? '' })
    const isTransactionPending = useIsTransactionPending(BlockchainTransactionType.RenewMembership)
    const isPending = isLoadingRenewMembership || isTransactionPending

    const handleClick = (getSigner: GetSigner) => {
        renewMembership(getSigner, 'Renew Sidebar')
    }

    if (shouldShowRenewMembership) {
        return (
            <SpaceSideBarListItem key="membership">
                <WalletReady LoginButton={<ReauthenticateButton />}>
                    {({ getSigner }) => (
                        <>
                            <RenewActionNavItem
                                text="Renew Membership"
                                isPending={isPending}
                                onClick={() => handleClick(getSigner)}
                            />
                        </>
                    )}
                </WalletReady>
            </SpaceSideBarListItem>
        )
    }

    return null
}

function RenewActionNavItem(props: {
    onClick: () => void
    text: string
    tooltip?: string
    isPending?: boolean
}) {
    const { onClick, text, tooltip, isPending } = props
    const spaceId = useSpaceIdFromPathname()
    const _onClick = useCallback(() => {
        if (isPending) {
            return
        }
        onClick()
    }, [onClick, isPending])
    return (
        <ActionNavItem
            icon={
                isPending ? (
                    <Box background="level2" rounded="sm" padding="sm">
                        <Box padding="xxs">
                            <ButtonSpinner />
                        </Box>
                    </Box>
                ) : (
                    <Box background="level2" rounded="sm" padding="sm">
                        {spaceId && <RenewCountdownCircle spaceId={spaceId} />}
                    </Box>
                )
            }
            minHeight="x5"
            tooltip={tooltip}
            id="membership"
            label={<Text color={isPending ? 'gray2' : 'error'}>{text}</Text>}
            onClick={_onClick}
        />
    )
}

function ReauthenticateButton() {
    const { privyLogin } = useCombinedAuth()
    return (
        <RenewActionNavItem
            text="Reauthenticate"
            tooltip="For your security, please click to re-authenticate with Privy."
            onClick={privyLogin}
        />
    )
}
